/**
 * @architecture-header 1
 * @layer module
 * @domain feedback
 * @responsibility Submit product feedback (bug / improvement / feature idea). Resending the same
 *   dedupeKey from the same actor returns the original submission, never a duplicate -- the
 *   response shape is identical whether the server answered 201 (created) or 200 (existing row),
 *   so the hook never needs to branch on status code.
 * @state-owner none -- one-shot mutation, no cached read to keep in sync (v1 has no list-back op)
 * @allowed-imports app environment; active-role provider; TanStack Query; zod
 * @forbidden routes; app bootstrap; direct Supabase client; localStorage
 * @public-api via modules/feedback/index.ts
 * @side-effects authenticated POST request outside demo mode
 * @naming-hook POST create -> useCreate<Resource> (named useSubmitFeedback: "submit" mirrors the
 *   approved operation id, an accepted naming exception for a one-shot report-style action)
 * @naming-path plural lowercase nouns; no version, role or verb segment
 * @naming-body camelCase; never the caller's role and never a server-side key -- the token says
 *   who, the path says what; organizationId is an explicit scope selector (precedent:
 *   activities.mutations.ts, assistants.mutations.ts), not an identity or role claim
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests workspace Playwright flows in frontend/apps/web/e2e/sh-f08.spec.ts
 */

import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveOrganizationId } from '../../../app/providers/ActiveRoleProvider';

export const feedbackCategorySchema = z.enum(['bug', 'improvement', 'feature_idea']);
export type FeedbackCategory = z.infer<typeof feedbackCategorySchema>;

export const productFeedbackSchema = z.object({
  id: z.string(),
  category: feedbackCategorySchema,
  createdAt: z.string(),
});
export type ProductFeedback = z.infer<typeof productFeedbackSchema>;

export function newFeedbackDedupeKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

export function useSubmitFeedback() {
  const environment = useWorkspaceEnvironment();
  const organizationId = useActiveOrganizationId();
  return useMutation({
    mutationFn: async ({
      category,
      text,
      dedupeKey,
    }: {
      category: FeedbackCategory;
      text: string;
      dedupeKey: string;
    }): Promise<ProductFeedback> => {
      if (!organizationId) throw new Error('FEEDBACK_NO_ACTIVE_ORGANIZATION');
      if (environment.demoMode) {
        return { id: `demo-feedback-${Date.now()}`, category, createdAt: new Date().toISOString() };
      }
      return productFeedbackSchema.parse(
        await environment.apiClient.request<unknown>('/api/v1/feedback', {
          method: 'POST',
          body: { organizationId, category, text, dedupeKey },
        }),
      );
    },
  });
}
